import type {
  AiProductImageIndexesResponse,
  AiProductImageIndexTenant
} from "__generated__/graphql"
import { ApiFormattedResponse } from "@models/api_formatted_response"
import { mutate, query } from "@utils/graphql"
import cancelHumanReplyLearningJob from "__generated__/queries/mutations/cancelHumanReplyLearningJob"
import setUpdateNotificationMutation from "__generated__/queries/mutations/setUpdateNotification"
import startReembedKnowledgeSourcesToContextualRag from "__generated__/queries/mutations/startReembedKnowledgeSourcesToContextualRag"
import getHumanReplyLearningJob from "__generated__/queries/queries/getHumanReplyLearningJob"
import getHumanReplyLearningJobs from "__generated__/queries/queries/getHumanReplyLearningJobs"
import getAiProductImageIndexes from "__generated__/queries/queries/getAiProductImageIndexes"
import getHumanReplyLearningStatus from "__generated__/queries/queries/getHumanReplyLearningStatus"
import getReembedKnowledgeSourcesToContextualRagJob from "__generated__/queries/queries/getReembedKnowledgeSourcesToContextualRagJob"
import getUpdateNotificationQuery from "__generated__/queries/queries/getUpdateNotification"

export type ReembedKnowledgeSourcesJobStatus =
  | "queued"
  | "running"
  | "completed"
  | "failed"

export type ReembedKnowledgeSourcesFailure = {
  customerId?: string | null
  aiKnowledgeSourceId?: string | null
  aiKnowledgeSourceContentId?: string | null
  reason?: string | null
}

export type ReembedKnowledgeSourcesToContextualRagJob = {
  jobId: string
  status: ReembedKnowledgeSourcesJobStatus
  allCustomers: boolean
  targetCustomerIds: string[]
  totalCustomers: number
  completedCustomers: number
  failedCustomers: number
  remainingCustomers: number
  processedKnowledgeSources: number
  processedContents: number
  reembeddedContents: number
  skippedContents: number
  failedContents: number
  currentCustomerId?: string | null
  completedCustomerIds: string[]
  failedCustomerIds: string[]
  failures: ReembedKnowledgeSourcesFailure[]
  createdAt: string
  startedAt?: string | null
  finishedAt?: string | null
  updatedAt: string
}

type ReembedKnowledgeSourcesToContextualRagJobResponse = {
  success?: boolean | null
  code?: string | null
  message?: string | null
  data?: ReembedKnowledgeSourcesToContextualRagJob | null
}

type ReembedKnowledgeSourcesToContextualRagInput = {
  allCustomers?: boolean
  customerIds?: string[]
}

export type HumanReplyLearningJobStatus =
  | "queued"
  | "running"
  | "cancelling"
  | "cancelled"
  | "completed"
  | "failed"

export type HumanReplyLearningFailure = {
  customerId?: string | null
  conversationId?: string | null
  reason?: string | null
}

export type HumanReplyLearningModelUsage = {
  model: string
  requests: number
  inputTokens: number
  outputTokens: number
  estimatedCost: number
}

export type HumanReplyLearningCostBreakdown = {
  stage: string
  model: string
  requests: number
  inputTokens: number
  outputTokens: number
  estimatedCost: number
}

export type HumanReplyLearningJob = {
  jobId: string
  customerId: string
  status: HumanReplyLearningJobStatus
  mode: "INITIAL" | "CONVERSATION"
  totalConversations: number
  processedConversations: number
  skippedConversations: number
  failedConversations: number
  remainingConversations: number
  progressPercent: number
  extractedPairs: number
  savedPairs: number
  duplicatePairs: number
  totalInputTokens: number
  totalOutputTokens: number
  totalEstimatedCost: number
  modelsUsed: string[]
  usageByModel: HumanReplyLearningModelUsage[]
  costBreakdown: HumanReplyLearningCostBreakdown[]
  currentConversationId?: string | null
  failures: HumanReplyLearningFailure[]
  createdAt: string
  startedAt?: string | null
  finishedAt?: string | null
  cancelRequestedAt?: string | null
  cancelledAt?: string | null
  countedAt?: string | null
  updatedAt: string
}

type HumanReplyLearningJobResponse = {
  success?: boolean | null
  code?: string | null
  message?: string | null
  data?: HumanReplyLearningJob | null
}

type HumanReplyLearningJobsResponse = {
  success?: boolean | null
  code?: string | null
  message?: string | null
  data?: HumanReplyLearningJob[] | null
}

export async function startReembedKnowledgeSourcesToContextualRagMutation(
  data: ReembedKnowledgeSourcesToContextualRagInput
): Promise<
  ApiFormattedResponse<ReembedKnowledgeSourcesToContextualRagJob | null>
> {
  const response = await mutate<{
    startReembedKnowledgeSourcesToContextualRag?: ReembedKnowledgeSourcesToContextualRagJobResponse
  }>({
    mutation: startReembedKnowledgeSourcesToContextualRag,
    variables: { data }
  })

  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data:
      response.data?.startReembedKnowledgeSourcesToContextualRag?.data ?? null
  }
}

export async function getReembedKnowledgeSourcesToContextualRagJobQuery(
  jobId: string
): Promise<
  ApiFormattedResponse<ReembedKnowledgeSourcesToContextualRagJob | null>
> {
  const response = await query<{
    getReembedKnowledgeSourcesToContextualRagJob?: ReembedKnowledgeSourcesToContextualRagJobResponse
  }>({
    query: getReembedKnowledgeSourcesToContextualRagJob,
    variables: { jobId },
    fetchPolicy: "network-only"
  })

  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data:
      response.data?.getReembedKnowledgeSourcesToContextualRagJob?.data ?? null
  }
}

export async function getHumanReplyLearningStatusQuery(
  customerId: string
): Promise<ApiFormattedResponse<HumanReplyLearningJob | null>> {
  const response = await query<{
    getHumanReplyLearningStatus?: HumanReplyLearningJobResponse
  }>({
    query: getHumanReplyLearningStatus,
    variables: { customerId },
    fetchPolicy: "network-only"
  })

  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.getHumanReplyLearningStatus?.data ?? null
  }
}

export async function getHumanReplyLearningJobQuery(
  customerId: string,
  jobId: string
): Promise<ApiFormattedResponse<HumanReplyLearningJob | null>> {
  const response = await query<{
    getHumanReplyLearningJob?: HumanReplyLearningJobResponse
  }>({
    query: getHumanReplyLearningJob,
    variables: { customerId, jobId },
    fetchPolicy: "network-only"
  })

  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.getHumanReplyLearningJob?.data ?? null
  }
}

export async function getHumanReplyLearningJobsQuery(
  customerId: string,
  limit = 10
): Promise<ApiFormattedResponse<HumanReplyLearningJob[]>> {
  const response = await query<{
    getHumanReplyLearningJobs?: HumanReplyLearningJobsResponse
  }>({
    query: getHumanReplyLearningJobs,
    variables: { customerId, limit },
    fetchPolicy: "network-only"
  })

  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.getHumanReplyLearningJobs?.data ?? []
  }
}

export async function cancelHumanReplyLearningJobMutation(
  customerId: string,
  jobId: string
): Promise<ApiFormattedResponse<HumanReplyLearningJob | null>> {
  const response = await mutate<{
    cancelHumanReplyLearningJob?: HumanReplyLearningJobResponse
  }>({
    mutation: cancelHumanReplyLearningJob,
    variables: { customerId, jobId }
  })

  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.cancelHumanReplyLearningJob?.data ?? null
  }
}

export type UpdateNotification = {
  updatedAt: string
  message: string | null
}

type UpdateNotificationResponse = {
  success?: boolean | null
  code?: string | null
  message?: string | null
  data?: UpdateNotification | null
}

export async function getUpdateNotification(): Promise<
  ApiFormattedResponse<UpdateNotification | null>
> {
  const response = await query<{
    getUpdateNotification?: UpdateNotificationResponse
  }>({
    query: getUpdateNotificationQuery,
    fetchPolicy: "network-only"
  })

  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.getUpdateNotification?.data ?? null
  }
}

export async function setUpdateNotification(
  message?: string
): Promise<ApiFormattedResponse<UpdateNotification | null>> {
  const response = await mutate<{
    setUpdateNotification?: UpdateNotificationResponse
  }>({
    mutation: setUpdateNotificationMutation,
    variables: { message }
  })

  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.setUpdateNotification?.data ?? null
  }
}

/** Every tenant with "use product images" on: progress, failures and embedding cost. */
export async function getAiProductImageIndexesQuery(): Promise<
  ApiFormattedResponse<AiProductImageIndexTenant[]>
> {
  const response = await query<{
    getAiProductImageIndexes?: AiProductImageIndexesResponse
  }>({
    query: getAiProductImageIndexes,
    fetchPolicy: "network-only"
  })

  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }

  return {
    isSuccess: true,
    data: response.data?.getAiProductImageIndexes?.data ?? []
  }
}
